import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, FolderOpen, Zap, Clock, Trash2, X, Archive } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ProjectCard from '../components/ProjectCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProject, setNewProject] = useState({ title: '', production_company: '', genre: '', logline: '' });
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState('');

  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const isSuperadmin = user?.global_role === 'superadmin';

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects?include_archived=true`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(response.data);
      setSelectedIds([]);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Derived project sets ─────────────────────────────────────────────
  const archivedProjects = useMemo(() => projects.filter(p => p.status === 'archived'), [projects]);
  const activeProjects   = useMemo(() => projects.filter(p => p.status !== 'archived'), [projects]);
  const visibleProjects  = viewMode === 'archived' ? archivedProjects : activeProjects;
  const demoProjects     = useMemo(() => viewMode === 'active' ? visibleProjects.filter(p => p.is_demo) : [], [viewMode, visibleProjects]);
  const userProjects     = useMemo(() => viewMode === 'active' ? visibleProjects.filter(p => !p.is_demo) : visibleProjects, [viewMode, visibleProjects]);

  // Stats — derived once from full project list.
  const stats = useMemo(() => {
    const total = projects.length;
    const active = activeProjects.length;
    const addedThisMonth = projects.filter(p => {
      if (!p.created_at) return false;
      const d = new Date(p.created_at);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const recent = projects
      .slice()
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))[0];
    const recentDate = recent
      ? new Date(recent.updated_at || recent.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    return { total, active, addedThisMonth, recent, recentDate };
  }, [projects, activeProjects]);

  // ── Handlers (unchanged behaviour) ────────────────────────────
  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project created!');
      setShowCreateDialog(false);
      setNewProject({ title: '', production_company: '', genre: '', logline: '' });
      navigate(`/projects/${response.data.id}/upload`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally { setCreating(false); }
  };

  const handleStartRename = (e, project) => {
    e.stopPropagation();
    setRenamingId(project.id);
    setRenameText(project.title);
  };

  const handleSaveRename = async (e, project) => {
    e.stopPropagation();
    const newTitle = renameText.trim();
    if (!newTitle || newTitle === project.title) { setRenamingId(null); return; }
    try {
      const r = await axios.patch(`${API}/projects/${project.id}`, { title: newTitle }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project renamed');
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, title: r.data.title } : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally { setRenamingId(null); }
  };

  const handleDeleteProject = async (e, project) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.title}"? This permanently removes the script, scenes, all breakdowns, gallery, palettes, budget and snapshots. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted "${project.title}"`);
      setProjects(prev => prev.filter(p => p.id !== project.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  const toggleSelect = (e, pid) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };
  const clearSelection = () => setSelectedIds([]);

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} project${selectedIds.length === 1 ? '' : 's'}? They will be hidden from the active list but recoverable.`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/projects/bulk-archive`, { project_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      const ok = r.data.archived?.length || 0;
      const failed = r.data.failed?.length || 0;
      if (ok) toast.success(`${ok} project${ok === 1 ? '' : 's'} archived`);
      if (failed) toast.error(`${failed} project${failed === 1 ? '' : 's'} could not be archived`);
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk archive failed');
    } finally { setBulkBusy(false); }
  };

  const openBulkDelete = () => { setDeleteConfirmText(''); setShowDeleteModal(true); };

  const handleBulkDeleteConfirm = async () => {
    if (deleteConfirmText !== 'DELETE' || selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API}/projects/bulk-delete`,
        { project_ids: selectedIds, confirm: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const ok = r.data.deleted?.length || 0;
      const failed = r.data.failed?.length || 0;
      if (ok) toast.success(`${ok} project${ok === 1 ? '' : 's'} permanently deleted`);
      if (failed) toast.error(`${failed} project${failed === 1 ? '' : 's'} could not be deleted`);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  const handleRestore = async (e, project) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/projects/${project.id}`, { status: 'active' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Restored "${project.title}"`);
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  const firstName = (user?.full_name || user?.name || 'Director').split(' ')[0];

  const cardProps = (project) => ({
    project, user, viewMode, selectedIds, renamingId, renameText,
    setRenameText, setRenamingId,
    navigate, toggleSelect,
    handleRestore, handleStartRename, handleDeleteProject, handleSaveRename,
    hasSelection: selectedIds.length > 0,
  });

  return (
    <main className="fp-main" data-testid="dashboard-page">
      {/* ── Topbar ─────────────────────────────────────────── */}
      <div className="fp-topbar">
        <div>
          <div className="fp-eyebrow">FiPr.ai Studio</div>
          <h1 className="fp-h1">Welcome back, {firstName}.</h1>
          <p className="fp-sub">Your productions, breakdowns, and schedules — beautifully unified in one place.</p>
        </div>
        <div className="fp-top-actions">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <button className="fp-btn-primary" data-testid="create-project-btn">
                <Plus className="h-4 w-4" strokeWidth={2.4} />
                New Project
              </button>
            </DialogTrigger>
            <DialogContent style={{ background: 'var(--fp-panel)', borderColor: 'var(--fp-line-2)', color: 'var(--fp-txt)' }}>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold tracking-tight" style={{ color: 'var(--fp-txt)' }}>New Production</DialogTitle>
                <DialogDescription style={{ color: 'var(--fp-txt-2)' }} className="text-sm">
                  Set up a new film production project for AI script breakdown.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4 mt-2">
                {[
                  { label: 'Project Title *', key: 'title', placeholder: 'The Great Film', required: true, testid: 'project-title-input' },
                  { label: 'Production Company', key: 'production_company', placeholder: 'Studio Name', required: false, testid: 'project-company-input' },
                  { label: 'Genre', key: 'genre', placeholder: 'Drama, Action, Thriller…', required: false, testid: 'project-genre-input' },
                ].map(({ label, key, placeholder, required, testid }) => (
                  <div key={key}>
                    <label className="text-xs uppercase tracking-[0.12em] font-semibold mb-1.5 block" style={{ color: 'var(--fp-txt-3)' }}>{label}</label>
                    <Input
                      value={newProject[key]}
                      onChange={e => setNewProject({ ...newProject, [key]: e.target.value })}
                      style={{ background: 'var(--fp-bg-2)', borderColor: 'var(--fp-line)', color: 'var(--fp-txt)' }}
                      className="focus:border-[var(--fp-amber)] focus:ring-0 text-sm"
                      placeholder={placeholder}
                      required={required}
                      data-testid={testid}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs uppercase tracking-[0.12em] font-semibold mb-1.5 block" style={{ color: 'var(--fp-txt-3)' }}>Logline</label>
                  <textarea
                    value={newProject.logline}
                    onChange={e => setNewProject({ ...newProject, logline: e.target.value })}
                    className="w-full rounded-md p-3 min-h-[72px] text-sm outline-none resize-none"
                    style={{ background: 'var(--fp-bg-2)', border: '1px solid var(--fp-line)', color: 'var(--fp-txt)' }}
                    placeholder="Brief story description…"
                    data-testid="project-logline-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={creating}
                  className="fp-btn-primary w-full justify-center"
                  data-testid="submit-create-project-btn"
                >
                  {creating ? 'Creating…' : 'Create Project'}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ── Stats ──────────────────────────────────────────── */}
      {!loading && (
        <section className="fp-stats" data-testid="dashboard-stats">
          <div className="fp-stat">
            <div className="fp-st-top">
              <span className="fp-st-label">Total Projects</span>
              <div className="fp-st-ico"><FolderOpen className="h-4 w-4" strokeWidth={2} /></div>
            </div>
            <div className="fp-st-val" data-testid="stat-total">{stats.total}</div>
            <div className="fp-st-meta">
              {stats.addedThisMonth > 0
                ? (<><span className="up">▲ {stats.addedThisMonth}</span> added this month</>)
                : 'No new projects this month'}
            </div>
          </div>
          <div className="fp-stat">
            <div className="fp-st-top">
              <span className="fp-st-label">Active</span>
              <div className="fp-st-ico"><Zap className="h-4 w-4" strokeWidth={2} /></div>
            </div>
            <div className="fp-st-val" data-testid="stat-active">{stats.active}</div>
            <div className="fp-st-meta">
              {stats.active > 0 ? 'All productions in motion' : 'No active productions'}
            </div>
          </div>
          <div className="fp-stat">
            <div className="fp-st-top">
              <span className="fp-st-label">Most Recent</span>
              <div className="fp-st-ico"><Clock className="h-4 w-4" strokeWidth={2} /></div>
            </div>
            <div className="fp-st-recent" data-testid="stat-recent-title" title={stats.recent?.title || ''}>
              {stats.recent?.title || '—'}
            </div>
            <div className="fp-st-meta">{stats.recent ? `Updated ${stats.recentDate}` : 'No projects yet'}</div>
          </div>
        </section>
      )}

      {/* ── Section header + segmented toggle + bulk action bar ─── */}
      <div className="fp-sec-head" data-testid="projects-toolbar">
        <div>
          <div className="fp-sec-title">
            {viewMode === 'archived' ? 'Archived Projects' : 'Demo Projects'}
            {viewMode === 'active' && demoProjects.length > 0 && (
              <span className="chip" data-testid="demo-count-chip">{demoProjects.length}</span>
            )}
          </div>
          <div className="fp-sec-sub">
            {viewMode === 'archived'
              ? 'Restore any production or clear it permanently.'
              : 'Explore FiPr.ai with pre-seeded scripts in English, Hindi & Malayalam.'}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {selectedIds.length > 0 && (
            <div className="fp-bulk-bar" data-testid="bulk-action-bar">
              <span className="fp-bulk-count" data-testid="bulk-selected-count">{selectedIds.length} selected</span>
              <button className="fp-bulk-btn fp-bulk-clear" onClick={clearSelection} data-testid="bulk-clear-btn">
                <X className="h-3 w-3" /> Clear
              </button>
              {viewMode === 'active' && (
                <button
                  className="fp-bulk-btn fp-bulk-archive"
                  onClick={handleBulkArchive}
                  disabled={bulkBusy}
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="h-3 w-3" /> Archive ({selectedIds.length})
                </button>
              )}
              {isSuperadmin && (
                <button
                  className="fp-bulk-btn fp-bulk-delete"
                  onClick={openBulkDelete}
                  disabled={bulkBusy}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3 w-3" /> Delete ({selectedIds.length})
                </button>
              )}
            </div>
          )}
          <div className="fp-segmented" role="tablist">
            {[
              { key: 'active', label: 'Active' },
              { key: 'archived', label: 'Archived' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={viewMode === key ? 'on' : ''}
                role="tab"
                aria-selected={viewMode === key}
                data-testid={`view-mode-${key}-btn`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      {loading ? (
        <div className="fp-grid" data-testid="projects-grid-loading">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse" style={{ height: 280, background: 'var(--fp-panel)', border: '1px solid var(--fp-line)', borderRadius: 'var(--fp-r)' }} />
          ))}
        </div>
      ) : visibleProjects.length === 0 ? (
        viewMode === 'archived' ? (
          <div className="fp-empty" data-testid="no-archived-card">
            <Archive className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--fp-txt-3)' }} strokeWidth={1} />
            <h2 className="text-base font-bold tracking-tight mb-1" style={{ color: 'var(--fp-txt)' }}>No archived projects</h2>
            <p className="text-sm max-w-sm mx-auto" style={{ color: 'var(--fp-txt-3)' }}>
              Archived productions will appear here. You can restore them anytime.
            </p>
          </div>
        ) : (
          <div className="fp-empty" data-testid="no-projects-card">
            <h2 className="text-2xl font-bold tracking-tight mb-2" style={{ color: 'var(--fp-txt)' }}>
              Every great film starts with a blank slate.
            </h2>
            <p className="text-sm mb-6 max-w-md mx-auto leading-relaxed" style={{ color: 'var(--fp-txt-2)' }}>
              Upload your screenplay and let FiPr AI break it down — scenes, cast, props, departments — in minutes.
            </p>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="fp-btn-primary"
              data-testid="create-first-project-btn"
            >
              <Plus className="h-4 w-4" strokeWidth={2.4} />
              Create Your First Project
            </button>
          </div>
        )
      ) : (
        <>
          {demoProjects.length > 0 && (
            <section className="fp-grid" data-testid="demo-projects-grid" style={{ marginBottom: 8 }}>
              {demoProjects.map(project => (
                <ProjectCard key={project.id} {...cardProps(project)} />
              ))}
            </section>
          )}
          {viewMode === 'active' && demoProjects.length > 0 && (
            <div className="fp-sec-head" style={{ marginTop: 32, marginBottom: 16 }}>
              <div>
                <div className="fp-sec-title">Your Projects <span className="chip">{userProjects.length}</span></div>
                <div className="fp-sec-sub">Productions you own or collaborate on.</div>
              </div>
            </div>
          )}
          {userProjects.length > 0 ? (
            <section className="fp-grid" data-testid="projects-grid">
              {userProjects.map(project => (
                <ProjectCard key={project.id} {...cardProps(project)} />
              ))}
            </section>
          ) : viewMode === 'active' && demoProjects.length > 0 ? (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="fp-invite-card"
              data-testid="invite-create-project"
              aria-label="Create your first project"
            >
              <Plus className="h-6 w-6 mb-2" style={{ color: 'var(--fp-amber)' }} strokeWidth={2.2} />
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fp-txt)' }}>Create your first project</div>
              <div style={{ fontSize: 12.5, color: 'var(--fp-txt-3)', marginTop: 4, maxWidth: 380 }}>
                Upload your screenplay and let FiPr AI break it down in minutes.
              </div>
            </button>
          ) : null}
        </>
      )}

      {/* ── Bulk Delete Confirmation Modal ─────────────────── */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => { if (!bulkBusy) { setShowDeleteModal(open); if (!open) setDeleteConfirmText(''); } }}>
        <DialogContent style={{ background: 'var(--fp-panel)', borderColor: '#dc2626' }} data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ color: '#dc2626' }}>
              <Trash2 className="h-5 w-5" /> Permanent Delete — {selectedIds.length} project{selectedIds.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--fp-txt-2)' }} className="text-sm">
              This action <strong>cannot be undone</strong>. All scripts, scenes, breakdowns, schedules, snapshots, files, and AI activity logs tied to {selectedIds.length === 1 ? 'this project' : 'these projects'} will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <label className="text-xs uppercase tracking-[0.12em] font-semibold block" style={{ color: 'var(--fp-txt-3)' }}>
              Type <span style={{ color: '#dc2626' }} className="font-mono">DELETE</span> to confirm
            </label>
            <Input
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              style={{ background: 'var(--fp-bg-2)', borderColor: deleteConfirmText === 'DELETE' ? '#dc2626' : 'var(--fp-line)', color: 'var(--fp-txt)' }}
              className="font-mono text-sm focus:ring-0"
              data-testid="bulk-delete-confirm-input"
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                disabled={bulkBusy}
                variant="outline"
                style={{ background: 'transparent', borderColor: 'var(--fp-line)', color: 'var(--fp-txt-2)' }}
                data-testid="bulk-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDeleteConfirm}
                disabled={deleteConfirmText !== 'DELETE' || bulkBusy}
                className="font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: '#dc2626', color: '#fff' }}
                data-testid="bulk-delete-confirm-btn"
              >
                {bulkBusy ? 'Deleting…' : `Permanently Delete ${selectedIds.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Dashboard;
